(() => {
  if (customElements.get('velvet-bundle-contents')) return;
  const query = `query BundleContents($id: ID!, $country: CountryCode, $after: String) @inContext(country: $country) {
    node(id: $id) {
      ... on ProductVariant {
        components(first: 30, after: $after) {
          nodes {
            quantity
            productVariant {
              title
              image { url(transform: { maxWidth: 320, maxHeight: 320 }) altText }
              product { title featuredImage { url(transform: { maxWidth: 320, maxHeight: 320 }) altText } }
            }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    }
  }`;

  class VelvetBundleContents extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.product = this.closest('velvet-product');
      this.list = this.querySelector('[data-bundle-list]');
      this.status = this.querySelector('[data-bundle-status]');
      this.retry = this.querySelector('[data-bundle-retry]');
      this.toggle = this.querySelector('[data-bundle-toggle]');
      this.mobileView = window.matchMedia('(max-width: 760px)');
      this.cache = new Map();
      this.mobileView.addEventListener('change', () => this.updateVisibleRows(), { signal: this.listeners.signal });
      this.product?.addEventListener('velvet:variant-change', event => this.load(event.detail.id), { signal: this.listeners.signal });
      this.retry.addEventListener('click', () => this.load(this.variantId, true), { signal: this.listeners.signal });
      this.toggle?.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.updateVisibleRows();
      }, { signal: this.listeners.signal });
      this.load(this.product?.querySelector('[data-current-variant]')?.value || this.dataset.variant);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.request?.abort();
    }

    async load(id, force = false) {
      id = String(id || '');
      if (!force && id === this.variantId && (this.pending || this.loaded)) return;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.variantId = id;
      this.pending = true;
      this.loaded = false;
      this.rows = null;
      this.list.hidden = true;
      this.retry.hidden = true;
      if (this.toggle) this.toggle.hidden = true;
      this.status.hidden = false;
      this.status.textContent = this.dataset.loading;
      this.setAttribute('aria-busy', 'true');
      // A stalled request must not leave the list loading indefinitely.
      const timeout = setTimeout(() => request.abort(), 12000);

      try {
        if (!id) throw new Error('Missing bundle variant');
        let components = !force && this.cache.get(id);
        if (!components) {
          components = [];
          let after = null;
          do {
            const response = await fetch('/api/2026-10/graphql.json', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ query, variables: { id: `gid://shopify/ProductVariant/${id}`, country: this.dataset.country || null, after } }),
              signal: request.signal
            });
            if (!response.ok) throw new Error('Bundle request failed');
            const payload = await response.json();
            const connection = payload.data?.node?.components;
            if (payload.errors?.length || !connection) throw new Error('Bundle contents unavailable');
            components.push(...connection.nodes);
            after = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
            if (connection.pageInfo.hasNextPage && !after) throw new Error('Invalid bundle pagination');
          } while (after);
          if (!components.length) throw new Error('No bundle components');
          this.cache.set(id, components);
        }
        if (this.request !== request || !this.isConnected) return;
        this.render(components);
        this.loaded = true;
        this.status.hidden = true;
        this.list.hidden = false;
      } catch (error) {
        if (this.request !== request || !this.isConnected) return;
        this.status.textContent = this.dataset.error;
        this.retry.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.pending = false;
          this.setAttribute('aria-busy', 'false');
        }
      }
    }

    render(components) {
      const rows = components.map(component => {
        const row = document.createElement('li');
        row.className = 'vp-bundle-row';
        const media = document.createElement('div');
        media.className = 'vp-bundle-media';
        const productTitle = component.productVariant.product.title;
        const photo = component.productVariant.image || component.productVariant.product.featuredImage;
        if (photo?.url) {
          const image = document.createElement('img');
          image.className = 'vp-bundle-image';
          image.src = photo.url;
          image.alt = photo.altText || productTitle;
          image.width = 320;
          image.height = 320;
          image.loading = 'lazy';
          image.decoding = 'async';
          media.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'vp-bundle-image-placeholder';
          placeholder.textContent = productTitle.charAt(0);
          placeholder.setAttribute('aria-hidden', 'true');
          media.append(placeholder);
        }
        const title = document.createElement('span');
        title.className = 'vp-bundle-title';
        title.textContent = productTitle;
        const variantTitle = component.productVariant.title;
        row.title = productTitle;
        if (this.dataset.showVariant === 'true' && variantTitle && variantTitle !== 'Default Title') {
          const variant = document.createElement('small');
          variant.className = 'vp-bundle-variant';
          variant.textContent = variantTitle;
          title.append(variant);
          row.title = `${productTitle} — ${variantTitle}`;
        }
        const quantity = document.createElement('span');
        quantity.className = 'vp-bundle-quantity';
        quantity.textContent = `× ${component.quantity}`;
        quantity.setAttribute('aria-label', `${this.dataset.quantityLabel}: ${component.quantity}`);
        row.append(media, title, quantity);
        return row;
      });
      this.list.replaceChildren(...rows);
      this.rows = rows;
      this.expanded = false;
      this.updateVisibleRows();
    }

    updateVisibleRows() {
      if (!this.toggle || !this.rows) return;
      const limit = Math.max(1, this.mobileView.matches
        ? Number(this.dataset.mobileVisibleCount) || 2
        : Number(this.dataset.visibleCount) || 4);
      const collapsible = this.dataset.collapseList !== 'false' && this.rows.length > limit;
      this.rows.forEach((row, index) => { row.hidden = collapsible && !this.expanded && index >= limit; });
      this.toggle.hidden = !collapsible;
      this.toggle.setAttribute('aria-expanded', String(!!this.expanded));
      this.toggle.textContent = this.expanded
        ? this.dataset.collapse
        : this.dataset.expand.replace('[count]', this.rows.length);
    }
  }

  customElements.define('velvet-bundle-contents', VelvetBundleContents);
})();
